import { boolean, number, object } from 'yup';
import { RequiredNumberSchema } from 'yup/lib/number';

import { IGNodeBConfigParametersValidationRule } from '@interfaces/crowdcell/gNodeBConfig';

import { EGNodeBConfigParametersValidationTypes } from '@enums/crowdCells';

import {
	getMinMaxDigitalValueErrorText,
	mandatoryErrMessage,
	validationErrMessages,
} from '@constants/validationConstants';

export const getSchema = (parameterName?: string, validationRules: IGNodeBConfigParametersValidationRule[] = []) => {
	return object({
		parameter: getParameterValidationSchema(parameterName, validationRules),
		isRestart: boolean(),
	});
};

export const getParameterValidationSchema = (
	parameterName?: string,
	validationRules: IGNodeBConfigParametersValidationRule[] = [],
): RequiredNumberSchema<number | undefined | null> => {
	if (!parameterName) return number().required(mandatoryErrMessage('Parameter'));

	const validationRule = validationRules?.find((rule) => rule.parameterName === parameterName);

	const parameterValidation: RequiredNumberSchema<number | undefined | null> = number()
		.required(mandatoryErrMessage(parameterName))
		.transform((value) => (isNaN(value) ? undefined : value))
		.nullable()
		.typeError(validationErrMessages.configParameterInvalidValue);

	if (!validationRule) {
		return parameterValidation;
	}

	if (validationRule?.validationType?.name === EGNodeBConfigParametersValidationTypes.listOfValues) {
		return number()
			.required(mandatoryErrMessage(parameterName))
			.transform((value) => (isNaN(value) ? undefined : value))
			.nullable()
			.oneOf(validationRule.validationList || [], validationErrMessages.configParameterInvalidValue)
			.typeError(validationErrMessages.configParameterInvalidValue);
	} else if (validationRule?.validationType?.name === EGNodeBConfigParametersValidationTypes.integerInterval
		&& validationRule.validationInt?.from !== undefined
		&& validationRule.validationInt?.to !== undefined
		&& Number.isInteger(validationRule.validationInt?.from)
		&& Number.isInteger(validationRule.validationInt?.to)) {
		return number()
			.required(mandatoryErrMessage(parameterName))
			.transform((value) => (isNaN(value) ? undefined : value))
			.nullable()
			.test('validateParameterValue', getMinMaxDigitalValueErrorText({
				name: parameterName,
				min: validationRule.validationInt.from,
				max: validationRule.validationInt.to,
			}), (value): boolean => {
				if (value === undefined || value === null || isNaN(value)) return false;

				return validationRule.validationInt?.from !== undefined
					&& validationRule.validationInt?.to !== undefined
					&& validationRule?.validationInt?.from <= value
					&& value <= validationRule?.validationInt?.to;
			});
	}

	return parameterValidation;
};
